import React, { useEffect, useMemo, useRef, useState } from "react";
import YearCalendar from "../../../shared/YearlyCalendar";
import Select from "react-select";
import { CSVLink } from "react-csv";
import API from "../../../services/api";
import { Toast } from "../../../shared/Toast";
import TableLoader from "../../../shared/TableLoader";
import NoDataFound from "../../../shared/NoDataFound";
import { BrandsTypes, getBrandOptions, toLakhs } from "../../../utils/constants";
import { useSelector } from "react-redux";
import BrandDetails from "./BrandDetails";

const FyPlanSummary = () => {
  const [teamType, setTeamType] = useState(2);
  const [userType, setUserType] = useState(-1);
  const [reportData, setReportData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const reviewReportsBase = "/review-reports";
  const [csvData, setCsvData] = useState([]);
  const csvLinkRef = useRef(null);
  const selectedYear = useSelector((state) => state.Head.selectedYear);
  const [showBrandDetails, setShowBrandDetails] = useState(false);
  const [brandDetailsUrl, setBrandDetailsUrl] = useState("");
  const [brandDetailsPayload, setBrandDetailsPayload] = useState(null);
  const [brandDetailsTitle, setBrandDetailsTitle] = useState("");

  const teamOptions = useMemo(() => {
    const options = getBrandOptions(userType, localStorage.getItem("email"));
    return options?.length ? options : BrandsTypes;
  }, [userType]);

  const rows = [
    {
      key: "eenadu_converted_customers",
      label: "Eenadu converted customers",
      detailUrl: "/review-reports/convertedCommonBrandWiseDetails",
    },
    {
      key: "eenadu_non_converted_customers",
      label: "Eenadu non-converted customers",
      detailUrl: "/review-reports/nonConvertedBrandWiseDetails",
    },
    {
      key: "w2n_exclusive_customers",
      label: "W2N Exclusive customers",
      detailUrl: "/review-reports/exclusiveBrandWiseDetails",
    },
    { key: "others_customers", label: "Others (Govt/ Initiatives/ Elections/ TN L2L etc.)" },
    { key: "eenadu_conversion_pct", label: "% Eenadu conversion", format: "percent" },
  ];

  useEffect(() => {
    const fetchFyPlanSummary = async () => {
      setIsLoading(true);
      try {
        const result = await API({
          url: `${reviewReportsBase}/getFyPlanSummary`,
          method: "POST",
          data: { brand_type: teamType, year: selectedYear },
        });

        if (result.status === "SUCCESS") {
          setReportData(result.data || {});
          if (result?.userType !== undefined && result?.userType !== null) {
            setUserType(result.userType);
          }
        } else {
          setReportData({});
          Toast("error", result?.message || "Failed To Fetch FY Plan Summary");
        }
      } catch (error) {
        setReportData({});
        Toast("error", "An error occurred");
      } finally {
        setIsLoading(false);
      }
    };

    fetchFyPlanSummary();
  }, [teamType, selectedYear]);

  const getCellValue = (rowKey, columnKey, format) => {
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    if (format === "percent" || columnKey === "pct_change") {
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) return raw;
      return `${numeric}%`;
    }
    if (columnKey.includes("revenues")) {
      return toLakhs(raw);
    }
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const parseNumeric = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string") {
      const cleaned = value.replace(/,/g, "").trim();
      if (cleaned === "") return null;
      const parsed = Number(cleaned);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const getRevenueTitle = (rowKey, columnKey, format) => {
    if (!columnKey.includes("revenues")) return undefined;
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    const parsed = parseNumeric(raw);
    if (parsed === null) return raw;
    if (format === "percent" || columnKey === "pct_change_revenues") return `${parsed}%`;
    return parsed;
  };
  const getCsvValue = (rowKey, columnKey, format) => {
    const raw = reportData?.[rowKey]?.[columnKey];
    if (raw === null || raw === undefined || raw === "") return "-";
    if (format === "percent" || columnKey === "pct_change") {
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) return raw;
      return `${numeric}%`;
    }
    const numeric = Number(raw);
    if (Number.isNaN(numeric)) return raw;
    return Math.round(numeric);
  };

  const handlePrepareCSV = () => {
    if (!reportData || Object.keys(reportData).length === 0) return;
    const rowsForCsv = rows.map((row) => ({
      Metric: row.label,
      [`${fyPrevLabel} No.`]: row.onlyRevenue ? "-" : getCsvValue(row.key, "fy25_no", row.format),
      [`${fyPrevLabel} Revenues`]: getCsvValue(row.key, "fy25_revenues", row.format),
      [`${fyPrevLabel} Insertions`]: getCsvValue(row.key, "fy25_insertions", row.format),
      [`${fyCurrentLabel} No.`]: row.onlyRevenue ? "-" : getCsvValue(row.key, "fy26_no", row.format),
      [`${fyCurrentLabel} Revenues`]: getCsvValue(row.key, "fy26_revenues", row.format),
      [`${fyCurrentLabel} Insertions`]: getCsvValue(row.key, "fy26_insertions", row.format),
      "% Change (No.)": getCsvValue(row.key, "pct_change_no", "percent"),
      "% Change (Revenues)": getCsvValue(row.key, "pct_change_revenues", "percent"),
      "% Change (Insertions)": getCsvValue(row.key, "pct_change_insertions", "percent"),
    }));

    setCsvData(rowsForCsv);
  };

  useEffect(() => {
    if (csvData.length > 0) {
      csvLinkRef.current.link.click();
      setCsvData([]);
    }
  }, [csvData]);

  const getCellClass = (columnKey, isTotal) => {
    const base = "border px-2 py-1 text-center";
    const groupBorder =
      columnKey === "fy25_no" || columnKey === "fy26_no"
        ? "border-l-2 border-gray-500"
        : "";
    const totalClass = isTotal ? "font-semibold bg-gray-50" : "";
    return `${base} ${groupBorder} ${totalClass}`.trim();
  };

  const formatDate = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDateRangeForColumn = (columnKey) => {
    const parsedYear = new Date(selectedYear).getFullYear();
    if (columnKey === "fy25_no" || columnKey === "fy25_revenues") {
      return {
        startDate: formatDate(new Date(parsedYear - 1, 0, 1)),
        endDate: formatDate(new Date(parsedYear - 1, 11, 31)),
      };
    }
    if (columnKey === "fy26_no" || columnKey === "fy26_revenues") {
      return {
        startDate: formatDate(new Date(parsedYear, 0, 1)),
        endDate: formatDate(new Date(parsedYear, 11, 31)),
      };
    }
    return null;
  };

  const handleOpenBrandDetails = (row, columnKey) => {
    if (!row?.detailUrl) return;
    if (!["fy25_no", "fy26_no"].includes(columnKey)) return;
    const dateRange = getDateRangeForColumn(columnKey);
    if (!dateRange) return;
    setBrandDetailsUrl(row.detailUrl);
    setBrandDetailsPayload({
      ...dateRange,
      brand_type: teamType,
    });
    const label = columnKey === "fy25_no" ? fyPrevLabel : fyCurrentLabel;
    setBrandDetailsTitle(`${row.label} - ${label}`);
    setShowBrandDetails(true);
  };

  const fyPrevLabel = reportData?.fyPrevLabel || "FY25";
  const fyCurrentLabel = reportData?.fyCurrentLabel || "FY26";

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="text-sm text-gray-700 font-semibold">Overall Plan Summary</div>
        <aside className="flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2 min-w-[240px]">
            <span className="font-semibold whitespace-nowrap">Team:</span>
            <Select
              className="min-w-[200px]"
              placeholder="Select Team"
              value={teamOptions.find((option) => option.value === teamType) || null}
              onChange={(selected) => setTeamType(selected ? selected.value : null)}
              options={teamOptions}
              isClearable
            />
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold whitespace-nowrap">Year:</span>
            <YearCalendar startYear={2023} endYear={2100} />
          </div>
          <CSVLink
            data={csvData}
            filename="fy_plan_summary.csv"
            className="hidden"
            ref={csvLinkRef}
          />
          {Object.keys(reportData || {}).length > 0 && !isLoading && (
            <button
              onClick={handlePrepareCSV}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Download Report
            </button>
          )}
        </aside>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-[980px] w-full border border-gray-300 text-sm bg-white">
          <thead>
            <tr className="bg-gray-100 text-center">
              <th className="border px-2 py-2 text-left" rowSpan={2}></th>
              <th className="border px-2 py-2" colSpan={3}>
                {fyPrevLabel}
              </th>
              <th className="border px-2 py-2 border-l-2 border-gray-500" colSpan={3}>
                {fyCurrentLabel}
              </th>
              <th className="border px-2 py-2 border-l-2 border-gray-500" colSpan={3}>
                % Change
              </th>
            </tr>
            <tr className="bg-gray-50 text-center">
              <th className="border px-2 py-2">No.</th>
              <th className="border px-2 py-2">Revenues</th>
              <th className="border px-2 py-2">Insertions</th>
              <th className="border px-2 py-2 border-l-2 border-gray-500">No.</th>
              <th className="border px-2 py-2">Revenues</th>
              <th className="border px-2 py-2">Insertions</th>
              <th className="border px-2 py-2 border-l-2 border-gray-500">No.</th>
              <th className="border px-2 py-2">Revenues</th>
              <th className="border px-2 py-2">Insertions</th>
            </tr>
          </thead>
          {isLoading ? (
            <TableLoader colspan="10" />
          ) : Object.keys(reportData || {}).length === 0 ? (
            <NoDataFound colspan="10" />
          ) : (
            <tbody>
              {rows.map((row) => (
                <tr key={row.key}>
                  <td className={getCellClass("label", row.isTotal)}>{row.label}</td>
                  <td
                    className={`${getCellClass("fy25_no", row.isTotal)} ${
                      row.detailUrl ? "text-blue-600  cursor-pointer" : ""
                    }`.trim()}
                    onClick={() => row.detailUrl && handleOpenBrandDetails(row, "fy25_no")}
                  >
                    {row.onlyRevenue ? "-" : getCellValue(row.key, "fy25_no", row.format)}
                  </td>
                  <td
                    className={getCellClass("fy25_revenues", row.isTotal)}
                    title={getRevenueTitle(row.key, "fy25_revenues", row.format)}
                  >
                    {getCellValue(row.key, "fy25_revenues", row.format)}
                  </td>
                  <td className={getCellClass("fy25_insertions", row.isTotal)}>
                    {getCellValue(row.key, "fy25_insertions", row.format)}
                  </td>
                  <td
                    className={`${getCellClass("fy26_no", row.isTotal)} ${
                      row.detailUrl ? "text-blue-600  cursor-pointer" : ""
                    }`.trim()}
                    onClick={() => row.detailUrl && handleOpenBrandDetails(row, "fy26_no")}
                  >
                    {row.onlyRevenue ? "-" : getCellValue(row.key, "fy26_no", row.format)}
                  </td>
                  <td
                    className={getCellClass("fy26_revenues", row.isTotal)}
                    title={getRevenueTitle(row.key, "fy26_revenues", row.format)}
                  >
                    {getCellValue(row.key, "fy26_revenues", row.format)}
                  </td>
                  <td className={getCellClass("fy26_insertions", row.isTotal)}>
                    {getCellValue(row.key, "fy26_insertions", row.format)}
                  </td>
                  <td className={getCellClass("pct_change", row.isTotal)}>
                    {getCellValue(row.key, "pct_change_no", "percent")}
                  </td>
                  <td
                    className={getCellClass("pct_change", row.isTotal)}
                    title={getRevenueTitle(row.key, "pct_change_revenues", "percent")}
                  >
                    {getCellValue(row.key, "pct_change_revenues", "percent")}
                  </td>
                  <td className={getCellClass("pct_change", row.isTotal)}>
                    {getCellValue(row.key, "pct_change_insertions", "percent")}
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>
       <BrandDetails
        open={showBrandDetails}
        onClose={() => setShowBrandDetails(false)}
        url={brandDetailsUrl}
        payload={brandDetailsPayload}
        title={brandDetailsTitle}
      />
    </div>
  );
};

export default FyPlanSummary;
